import { useMutation } from '@apollo/client';
import { gql } from '__generated__/gql';
import { FC, useState } from 'react';

import Button from 'primitives/Button';
import ConfirmButton from 'primitives/ConfirmButton';
import UploadInput from 'primitives/UploadInput';

import ErrorMessage from 'components/ErrorMessage';

const ADD_POST_ATTACHMENTS_MUTATION = gql(`
  mutation AddPostAttachments($postId: ID!, $attachments: [Upload]) {
    addPostAttachments(postId: $postId, attachments: $attachments) {
      id
      attachments {
        id
        url
        name
      }
    }
  }
`);

const REMOVE_POST_ATTACHMENT_MUTATION = gql(`
  mutation RemovePostAttachment($id: ID!) {
    removePostAttachment(id: $id) {
      id
      attachments {
        id
        url
        name
      }
    }
  }
`);

type Attachment = {
  id: string;
  url: string;
  name: string;
};

const PostAttachments: FC<{ postId: string; attachments: Attachment[] }> = ({
  postId,
  attachments,
}) => {
  const [stagedFiles, setStagedFiles] = useState<File[]>([]);

  const [addPostAttachments, { loading: addLoading, error: addError }] = useMutation(
    ADD_POST_ATTACHMENTS_MUTATION,
    { refetchQueries: ['getPost'] }
  );

  const [removePostAttachment, { loading: removeLoading, error: removeError }] = useMutation(
    REMOVE_POST_ATTACHMENT_MUTATION,
    { refetchQueries: ['getPost'] }
  );

  const handleUpload = () => {
    if (stagedFiles.length === 0) return;
    addPostAttachments({
      variables: { postId, attachments: stagedFiles },
    }).then(() => setStagedFiles([]));
  };

  return (
    <div className="mt-4">
      <h3 className="text-sm font-medium text-gray-900">Attachments</h3>

      {attachments.length > 0 ? (
        <ul className="mt-2 divide-y divide-gray-100 rounded-md border border-gray-200">
          {attachments.map(attachment => (
            <li
              key={attachment.id}
              className="flex items-center justify-between py-2 px-4 hover:bg-gray-50"
            >
              <span className="truncate text-sm text-gray-500">{attachment.name}</span>
              <div className="flex items-center gap-4">
                <a
                  href={attachment.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm font-medium text-blue-600 hover:text-blue-500"
                >
                  View
                </a>
                <ConfirmButton
                  variant="secondary"
                  className="!text-red-500"
                  loading={removeLoading}
                  error={removeError}
                  title="Remove attachment"
                  description={`Are you sure you want to remove "${attachment.name}"? This cannot be undone.`}
                  confirmButtonLabel="Remove"
                  onConfirm={() => removePostAttachment({ variables: { id: attachment.id } })}
                >
                  Remove
                </ConfirmButton>
              </div>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-sm text-gray-500">No attachments yet.</p>
      )}

      <div className="mt-4">
        <UploadInput
          fullWidth
          label="Add attachments"
          value={stagedFiles}
          onChange={setStagedFiles}
        />
        {addError && <ErrorMessage error={addError} />}
        <div className="mt-2 flex justify-end">
          <Button
            loading={addLoading}
            disabled={stagedFiles.length === 0 || addLoading}
            onClick={handleUpload}
          >
            {stagedFiles.length > 0 ? `Upload ${stagedFiles.length} file(s)` : 'Upload'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default PostAttachments;
